import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './icons';
import { cx } from './primitives';

export interface ListOption {
  value: string;
  label: string;
  description?: string;
  icon?: IconName;
  badge?: string;
  disabled?: boolean;
  group?: string;
}

export interface ListboxProps {
  id?: string;
  label?: string;
  heading?: string;
  name?: string;
  value?: string;
  defaultValue?: string;
  options: ListOption[];
  onChange?: (event: { target: { value: string; name: string } }) => void;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  searchable?: boolean;
  error?: boolean;
  required?: boolean;
  className?: string;
  trigger?: 'field' | 'chip' | 'ghost';
  align?: 'start' | 'end';
  leading?: React.ReactNode;
  form?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

const RATIO = /^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)/;

export function portalHost(anchor: HTMLElement | null): HTMLElement {
  return anchor?.closest<HTMLElement>('dialog[open]') || document.body;
}

export function useAnchoredPanel(
  open: boolean,
  trigger: React.RefObject<HTMLElement | null>,
  panel: React.RefObject<HTMLElement | null>,
  align: 'start' | 'end' = 'start',
) {
  const [style, setStyle] = useState<React.CSSProperties>({ position: 'fixed', visibility: 'hidden' });
  const place = useCallback(() => {
    const node = trigger.current;
    const menu = panel.current;
    if (!node || !menu) return;
    const rect = node.getBoundingClientRect();
    const gap = 8;
    const minWidth = Math.max(280, align === 'start' ? rect.width : 280);
    const width = Math.max(menu.offsetWidth, minWidth);
    const height = menu.offsetHeight || 320;
    const below = window.innerHeight - rect.bottom - gap;
    const above = rect.top - gap;
    const openUp = below < Math.min(height, 260) && above > below;
    let top = openUp ? rect.top - height - gap : rect.bottom + gap;
    let left = align === 'end' ? rect.right - width : rect.left;
    const maxHeight = Math.min(window.innerHeight - 16, 480);
    left = Math.min(Math.max(8, left), Math.max(8, window.innerWidth - width - 8));
    top = Math.min(Math.max(8, top), Math.max(8, window.innerHeight - Math.min(height, maxHeight) - 8));
    setStyle({
      position: 'fixed',
      top,
      left,
      minWidth,
      maxHeight,
      zIndex: 100,
      visibility: 'visible',
    });
  }, [align, panel, trigger]);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const frame = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  return style;
}

function RatioGlyph({ ratio }: { ratio: string }) {
  const match = RATIO.exec(ratio);
  if (!match) return null;
  const w = Number(match[1]);
  const h = Number(match[2]);
  if (!w || !h) return null;
  const max = 16;
  const scale = w >= h ? max / w : max / h;
  return (
    <span className="bs-listbox-ratio" aria-hidden="true">
      <span style={{ width: Math.max(7, w * scale), height: Math.max(7, h * scale) }} />
    </span>
  );
}

export function optionWell(option: ListOption) {
  if (option.icon) return <Icon name={option.icon} size={16} />;
  const ratioSource = option.label || option.value;
  if (RATIO.test(ratioSource)) return <RatioGlyph ratio={ratioSource} />;
  return null;
}

export function ListboxItem({
  id,
  option,
  selected = false,
  active = false,
  onSelect,
  role = 'option',
  className,
}: {
  id?: string;
  option: ListOption;
  selected?: boolean;
  active?: boolean;
  onSelect?: () => void;
  role?: 'option' | 'menuitem';
  className?: string;
}) {
  const well = optionWell(option);
  return (
    <button
      type="button"
      id={id}
      role={role}
      aria-selected={role === 'option' ? selected : undefined}
      disabled={option.disabled}
      className={cx('bs-listbox-item', selected && 'is-selected', active && 'is-active', className)}
      onClick={onSelect}
    >
      {well ? <span className="bs-listbox-well">{well}</span> : null}
      <span className="bs-listbox-copy">
        <span className="bs-listbox-title">
          {option.label}
          {option.badge ? <span className="bs-listbox-badge">{option.badge}</span> : null}
        </span>
        {option.description ? <small>{option.description}</small> : null}
      </span>
      {selected ? <Icon name="check" size={16} className="bs-listbox-check" /> : <span className="bs-listbox-check" />}
    </button>
  );
}

export function Listbox({
  id: passedId,
  label,
  heading,
  name,
  value,
  defaultValue,
  options,
  onChange,
  onValueChange,
  disabled,
  searchable,
  error,
  required,
  className,
  trigger = 'field',
  align = 'start',
  leading,
  form,
  ...aria
}: ListboxProps) {
  const uid = useId();
  const id = passedId || uid;
  const listId = `${id}-list`;
  const searchId = `${id}-search`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(-1);
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState(() => String(value ?? defaultValue ?? options[0]?.value ?? ''));
  const current = isControlled ? String(value) : internal;
  const selected = options.find((item) => item.value === current);
  const showSearch = searchable ?? options.length >= 8;
  const panelStyle = useAnchoredPanel(open, triggerRef, panelRef, align);
  const panelHeading = heading || label || aria['aria-label'];

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((item) =>
      `${item.label} ${item.description || ''} ${item.badge || ''}`.toLowerCase().includes(needle),
    );
  }, [options, query]);

  const emit = useCallback(
    (next: string) => {
      if (!isControlled) setInternal(next);
      onValueChange?.(next);
      onChange?.({ target: { value: next, name: name || '' } });
    },
    [isControlled, name, onChange, onValueChange],
  );

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActive(-1);
    triggerRef.current?.focus();
  }, []);

  const pick = useCallback(
    (next: string) => {
      emit(next);
      close();
    },
    [close, emit],
  );

  useEffect(() => {
    if (!open) return;
    const enabled = filtered.filter((item) => !item.disabled);
    const selectedIndex = filtered.findIndex((item) => item.value === current && !item.disabled);
    setActive(selectedIndex >= 0 ? selectedIndex : enabled.length ? filtered.indexOf(enabled[0]) : -1);
    const idFrame = requestAnimationFrame(() => {
      if (showSearch) searchRef.current?.focus();
      else panelRef.current?.querySelector<HTMLButtonElement>('.bs-listbox-item:not(:disabled)')?.focus();
    });
    function onPointer(event: PointerEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
      setQuery('');
      setActive(-1);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        close();
      }
    }
    document.addEventListener('pointerdown', onPointer);
    window.addEventListener('keydown', onKey, true);
    return () => {
      cancelAnimationFrame(idFrame);
      document.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [close, current, filtered, open, showSearch]);

  useEffect(() => {
    if (!open || active < 0) return;
    const buttons = panelRef.current?.querySelectorAll<HTMLButtonElement>('.bs-listbox-item');
    buttons?.[active]?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  function move(delta: number) {
    if (!filtered.length) return;
    let index = active;
    for (let step = 0; step < filtered.length; step += 1) {
      index = (index + delta + filtered.length) % filtered.length;
      if (!filtered[index]?.disabled) {
        setActive(index);
        return;
      }
    }
  }

  function onListKey(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(filtered.findIndex((item) => !item.disabled));
    } else if (event.key === 'End') {
      event.preventDefault();
      const last = [...filtered].reverse().find((item) => !item.disabled);
      if (last) setActive(filtered.lastIndexOf(last));
    } else if (event.key === 'Enter' || event.key === ' ') {
      if (event.currentTarget !== searchRef.current || event.key === 'Enter') {
        const item = filtered[active];
        if (item && !item.disabled) {
          event.preventDefault();
          pick(item.value);
        }
      }
    }
  }

  const panel = open
    ? createPortal(
        <div
          ref={panelRef}
          className="bs-theme bs-listbox-panel"
          style={panelStyle}
          onKeyDown={onListKey}
        >
          {panelHeading ? <p className="bs-listbox-heading">{panelHeading}</p> : null}
          {showSearch ? (
            <div className="bs-listbox-search">
              <Icon name="search" size={15} />
              <input
                ref={searchRef}
                id={searchId}
                type="search"
                value={query}
                placeholder="Search..."
                aria-label="Search options"
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActive(0);
                }}
              />
            </div>
          ) : null}
          <div
            id={listId}
            role="listbox"
            aria-label={panelHeading}
            aria-activedescendant={filtered[active] ? `${id}-opt-${active}` : undefined}
            className="bs-listbox-list"
          >
            {filtered.length ? (
              filtered.map((item, index) => {
                const prev = filtered[index - 1];
                const showGroup = Boolean(item.group && item.group !== prev?.group);
                return (
                  <React.Fragment key={`${item.group || ''}:${item.value}:${index}`}>
                    {showGroup ? <p className="bs-listbox-heading">{item.group}</p> : null}
                    <ListboxItem
                      id={`${id}-opt-${index}`}
                      option={item}
                      selected={item.value === current}
                      active={index === active}
                      onSelect={() => pick(item.value)}
                    />
                  </React.Fragment>
                );
              })
            ) : (
              <p className="bs-listbox-empty">No matches</p>
            )}
          </div>
        </div>,
        portalHost(triggerRef.current),
      )
    : null;

  return (
    <div className={cx('bs-listbox', `bs-listbox--${trigger}`, className)}>
      <button
        type="button"
        ref={triggerRef}
        id={id}
        className={cx('bs-listbox-trigger', `bs-listbox-trigger--${trigger}`, error && 'is-invalid')}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-invalid={error || undefined}
        aria-label={aria['aria-label'] || (trigger !== 'field' ? label : undefined)}
        aria-labelledby={aria['aria-labelledby']}
        aria-describedby={aria['aria-describedby']}
        onClick={() => !disabled && setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {leading}
        <span className="bs-listbox-value">{selected?.label || 'Select'}</span>
        <Icon name="down" size={14} />
      </button>
      {panel}
      {name ? (
        <select
          className="bs-sr-only"
          tabIndex={-1}
          aria-hidden="true"
          name={name}
          form={form}
          required={required}
          value={current}
          onChange={() => {}}
        >
          {options.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}
